import { useState } from "react";
import { Text, Space, Button, Loader } from "@mantine/core";
import { useMutation, useQuery } from "@apollo/client";
import { notifications } from "@mantine/notifications";

import { UPDATE_ORDER } from "../../gql/orders";
import { GetOrderEstimatedDeliveryDateDocument } from "../../gql/generated/graphql";
import { STYLE } from "../../types/constants";
import { getLastShippingDateFromDeliveryDate } from "../../utils/order.utils";
import { toDateStr } from "../../utils/date.utils";
import CustomDateSelection from "../recap/customDateSelection";

const UpdateDeliveryDateModal = ({ order, closeModal, refetch }) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    order?.deliveryDate ? new Date(order.deliveryDate) : null
  );

  const [updateOrder] = useMutation(UPDATE_ORDER);

  const {
    data: estimatedDeliveryData,
    loading: estimatedDeliveryLoading,
    error: estimatedDeliveryError,
    refetch: refetchEstimatedDelivery,
  } = useQuery(GetOrderEstimatedDeliveryDateDocument, {
    variables: { orderId: order.id, useConfirmedAtAsReferenceDate: true },
  });

  const estimatedDelivery =
    estimatedDeliveryData?.getOrderEstimatedDeliveryDate;

  const submitNewDeliveryDate = async () => {
    if (!selectedDate) {
      notifications.show({
        title: "Delivery date cannot be empty",
        color: "red",
        message: "",
      });
      return;
    }

    const newDeliveryDateStr = toDateStr(selectedDate);
    const isRush =
      !!estimatedDelivery &&
      newDeliveryDateStr < toDateStr(new Date(estimatedDelivery.deliveryDate));

    try {
      await updateOrder({
        variables: {
          where: { id: order.id },
          data: {
            deliveryDate: { set: newDeliveryDateStr },
            lastShippingDate: {
              set: getLastShippingDateFromDeliveryDate(selectedDate),
            },
            isRush: { set: isRush },
          },
        },
      });
      notifications.show({
        title: "Delivery date updated",
        color: "green",
        message: "",
      });
      refetch();
      closeModal();
    } catch (e) {
      console.error(e);
      notifications.show({
        title: "Error while trying to update delivery date",
        color: "red",
        message: "",
      });
    }
  };

  const renderCalendar = () => {
    if (estimatedDeliveryError) {
      return (
        <>
          <Text c="red">Failed to load delivery dates.</Text>
          <Space h="sm" />
          <Button
            style={{ backgroundColor: STYLE.primary }}
            onClick={() => refetchEstimatedDelivery()}
          >
            Retry
          </Button>
        </>
      );
    }
    if (estimatedDeliveryLoading || !estimatedDelivery) {
      return <Loader color={STYLE.primary} />;
    }
    return (
      <CustomDateSelection
        selectDate={setSelectedDate}
        optimalDeliveryDate={new Date(estimatedDelivery.deliveryDate)}
        minimalRushDeliveryDate={
          new Date(estimatedDelivery.minimalRushDeliveryDate)
        }
        minimalDate={new Date()}
        selectedDate={selectedDate}
        bankHolidays={estimatedDelivery.bankHolidays}
      />
    );
  };

  return (
    <div style={{ textAlign: "center" }}>
      <Text>
        Actual delivery date: {new Date(order.deliveryDate).toDateString()}
      </Text>
      <Space h="md" />
      {renderCalendar()}
      {!estimatedDeliveryError && (
        <>
          <Space h="md" />
          <Button
            style={{ backgroundColor: STYLE.primary }}
            onClick={submitNewDeliveryDate}
            disabled={!selectedDate}
          >
            Submit new delivery date
          </Button>
        </>
      )}
    </div>
  );
};

export default UpdateDeliveryDateModal;
